import { useState, useEffect, useCallback, useRef } from "react";
import { Search, Loader2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { searchPlayers, searchTeams } from "../../services/search-service";
import { getHistory, addToHistory, removeFromHistory } from "./utils/search-history";
import { DropdownContent } from "./components/dropdown-content";

export const PlayerTeamSearch = ({ collapsed = false }) => {
  const [query, setQuery] = useState("");
  const [playerResults, setPlayerResults] = useState([]);
  const [teamResults, setTeamResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState(getHistory);

  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const controllerRef = useRef(null);
  const searchRef = useRef(null);

  const allItems = query.trim()
    ? [...playerResults.map((p) => ({ ...p, _type: "player" })), ...teamResults.map((t) => ({ ...t, _type: "team" }))]
    : history;

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setPlayerResults([]);
      setTeamResults([]);
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setIsLoading(true);
    try {
      const [players, teams] = await Promise.all([
        searchPlayers(q, controller.signal).catch(() => []),
        searchTeams(q, controller.signal).catch(() => []),
      ]);
      if (!controller.signal.aborted) {
        setPlayerResults((Array.isArray(players) ? players : []).slice(0, 5));
        setTeamResults((Array.isArray(teams) ? teams : []).slice(0, 5));
        setIsOpen(true);
      }
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, []);

  const handleSelect = useCallback(
    (item) => {
      const isTeam = item._type === "team" || item.type === "team";
      const id = isTeam ? item.team_id || item.id : item.player_id || item.id;
      const name = isTeam ? item.team_name || item.name : item.player_name || item.name;
      const teamId = isTeam ? item.team_id || item.id : item.team_id;
      const teamName = isTeam ? item.team_name || item.name : item.team_name;
      const subtitle = isTeam
        ? [item.conference, item.division].filter(Boolean).join(" · ")
        : [item.team_name, item.conference].filter(Boolean).join(" · ");

      addToHistory({ type: isTeam ? "team" : "player", id, name, teamId, teamName, subtitle });
      navigate(isTeam ? `/team/${id}` : `/player/${id}`);
      handleClose();
    },
    [navigate]
  );

  const handleClose = () => {
    setQuery("");
    setPlayerResults([]);
    setTeamResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setPlayerResults([]);
      setTeamResults([]);
      setIsOpen(true);
      return;
    }

    debounceRef.current = setTimeout(() => runSearch(value), 250);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0 && allItems[selectedIndex]) {
      e.preventDefault();
      handleSelect(allItems[selectedIndex]);
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  const handleFocus = () => {
    setHistory(getHistory());
    setIsOpen(true);
  };

  const handleRemoveHistory = (id) => {
    removeFromHistory(id);
    setHistory(getHistory());
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      controllerRef.current?.abort();
    };
  }, []);

  const dropdownProps = {
    query,
    isLoading,
    playerResults,
    teamResults,
    history,
    selectedIndex,
    setSelectedIndex,
    onSelect: handleSelect,
    onRemoveHistory: handleRemoveHistory,
  };

  if (collapsed) {
    return (
      <div className="relative" ref={searchRef}>
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            setHistory(getHistory());
          }}
          className="w-full h-10 flex items-center justify-center text-gray-500 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all duration-200"
        >
          <Search className="w-4 h-4" />
        </button>

        {isOpen && (
          <div className="absolute left-full top-0 ml-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search players & teams..."
                  className="w-full h-10 pl-10 pr-10 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  autoComplete="off"
                  autoFocus
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button
                  onClick={handleClose}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <DropdownContent {...dropdownProps} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder="Search players & teams..."
          className="w-full h-10 pl-10 pr-10 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all duration-200 placeholder:text-gray-400"
          autoComplete="off"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
        {query && (
          <button
            onClick={handleClose}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <DropdownContent {...dropdownProps} />
        </div>
      )}
    </div>
  );
};
